/**
 * Integration detail configs — verbatim port of screens-integration-detail.jsx
 * (INTEGRATION_CONFIGS), re-keyed to this app's provider ids and made
 * tenant-agnostic (fictional Acme tenant; never real tenant names).
 * Each integration declares its auth method, required inputs, scopes, docs,
 * optional DNS records / callback URL, and recent activity. Integrations that
 * still need real input carry `needsInput` + `missing` so the detail page can
 * surface a "Required input" banner before Connect can succeed.
 */

export interface IntegInput {
  l: string; placeholder: string;
  type: "text" | "password" | "select" | "toggle" | "mapping" | "readonly" | "date";
  required: boolean; filled: string | boolean;
  connectedFilled?: string; // value shown only once the integration is connected
  // Display-only: value is provisioned by the backend (e.g. an OAuth-connected
  // account id / currency) and must not be user-editable even with write
  // access. ConfigField honors it (stripe-fe handoff §3, 2026-07-23).
  readonly?: boolean;
  // Helper text rendered below the field (payments work-order 2026-07-24 Task 2:
  // what it is, whether it's provisioned automatically, any constraint).
  desc?: string;
  // Shared app-registration addendum (kit 9620814f, 2026-07-24): a stored value
  // arrives configured/masked → render read-only with a "Change" affordance.
  configured?: boolean;
  masked?: boolean;
  fieldKey?: string;
}
export interface DnsRecord { type: string; host: string; value: string; status: "verified" | "warn" | "pending"; }
export interface GuestSyncOption { id: string; label: string; sub: string; recommended?: boolean; }
export interface FieldMapRow { jrni: string; sfLead: string; sfContact: string; required: boolean; notes: string; }
export interface IntegConfig {
  name: string; brand: string; icon: string; auth: string; desc: string;
  inputs: IntegInput[]; permissions: string[]; docs: string;
  callback?: string; dnsRecords?: DnsRecord[];
  /** Recent-activity rows — server-fed only (GM9/GM10 pushbacks): never bake invented metrics; empty until a contracted activity-log op exists. */
  activity: { at: string; e: string }[];
  needsInput?: boolean; missing?: string;
  guestSync?: { mode: string; options: GuestSyncOption[]; dedupe: string; onUpdate: string };
  fieldMap?: FieldMapRow[];
  /** Key into SHARED_CREDENTIAL_GROUPS — this integration's credentials live on a shared app registration, not on the integration itself. */
  sharedCredentials?: string;
}

const t = (l: string, placeholder: string, required: boolean, filled: string | boolean = "", type: IntegInput["type"] = "text", connectedFilled?: string): IntegInput =>
  ({ l, placeholder, type, required, filled, connectedFilled });

/* Shared app-registration credential groups: several integrations are backed by
 * ONE provider-side app registration, feature-toggled per integration. The
 * overview keeps one tile per integration; every member's detail page renders
 * THIS shared credential set instead of per-integration copies. */
export interface SharedFeature { id: string; integration?: string; name: string; desc: string; scopes: string[]; addon?: boolean; }
export interface SharedCredGroup {
  name: string; shortName: string; auth: string; brand: string; icon: string; logoId: string; desc: string;
  members: string[]; inputs: IntegInput[];
  /** Feature toggles on this app registration. `integration` links a catalog tile; registration-level features (e.g. mail) have none. Toggle provider id = backendProviderId(integration) ?? id. */
  features: SharedFeature[];
  activity: { at: string; e: string }[];
  /** Per-scope metadata for the shared consent: purpose note, Delegated vs Application (admin consent), optional. */
  scopeMeta: Record<string, { note: string; delegated?: boolean; optional?: boolean }>;
}
export const SHARED_CREDENTIAL_GROUPS: Record<string, SharedCredGroup> = { // ui-config — shared app-registration groups; tenant-agnostic
  "ms-app": {
    name: "Microsoft app registration",
    shortName: "Microsoft",
    auth: "OAuth 2.0 · Azure AD",
    brand: "#0078D4", icon: "M", logoId: "ms-app",
    desc: "One Azure AD app registration controls the Microsoft 365 Calendar, Microsoft Entra ID and Microsoft Teams features. Credentials are shared — each feature is switched on or off individually.",
    members: ["outlook", "entra", "teams"],
    inputs: [
      t("Application (Client) ID", "Registered Azure AD app ID", true, "a93f2210-77c4-4e9a-8b21-5d6f0c3e1abc"),
      t("Client Secret", "Azure AD app secret value", true, "q7X8Q~kP••••••••••••••••••••", "password"),
      t("Tenant ID", "Azure AD directory (tenant) ID", true, "6c1e8a22-9f0b-4d3e-bb71-2a4f1e9c7d50"),
      t("Client secret expiry date", "Expiry set on the secret in the Azure portal", true, "2027-03-31", "date"),
    ],
    features: [ // ui-config — static feature-toggle catalog (name/desc/scopes) for the shared registration; tenant-agnostic
      { id: "outlook", integration: "outlook", name: "Microsoft 365 Calendar", desc: "Two-way calendar sync for events and appointments.", scopes: ["Calendars.ReadWrite"] },
      { id: "entra", integration: "entra", name: "Microsoft Entra ID", desc: "Staff sign-in (SSO) and automatic directory sync.", scopes: ["User.Read", "User.Read.All", "GroupMember.Read.All"] },
      { id: "teams", integration: "teams", name: "Microsoft Teams", desc: "Auto-provision Teams meetings for virtual and hybrid events.", scopes: ["OnlineMeetings.ReadWrite.All"] },
      { id: "msmail", name: "Email sending", desc: "Outgoing email (e.g. booking notifications) sent through your own Microsoft 365 tenant.", scopes: ["Mail.Send"] },
      { id: "msgraphlog", name: "Graph API logging", desc: "Audit log of every Graph API call — calendar sync, Teams meetings, user queries — with retention, filtering and CSV export.", scopes: [], addon: true },
    ],
    // GM9 eval pushback #1 (2026-07-22): fabricated activity metrics removed —
    // mirrors the same-drop Stripe cleanup (activity: []). Real rows come from a
    // contracted activity-log op when one exists; never invented numbers in the UI.
    activity: [],
    scopeMeta: {
      "Calendars.ReadWrite": { note: "Two-way calendar sync for events and appointments." },
      "Mail.Send": { note: "Send outgoing email (e.g. booking notifications) through your own Microsoft 365 tenant instead of our mail infrastructure.", optional: true },
      "User.Read": { note: "Sign-in and reading the signed-in user's profile during setup.", delegated: true },
      "User.Read.All": { note: "Read full user profiles to match calendar data to the right people and keep staff in sync." },
      "GroupMember.Read.All": { note: "Read assigned role-group membership so staff sync queries groups directly — read-only, much faster than per-user checks.", optional: true },
      "OnlineMeetings.ReadWrite.All": { note: "Create and manage Teams meetings on behalf of users for virtual and hybrid events." },
    },
  },
};

/** Per-scope explanatory copy for the shared-registration consent card: what the
 * scope is FOR, Delegated vs Application, optional. ui-config COPY only — the
 * scope list itself always comes from the backend record's features.
 * (Restored 2026-07-27: the 2026-07-24 rewrite left `SHARED_CREDENTIAL_GROUPS`
 * render-dead and dropped this copy off the page; this helper makes the
 * scopeMeta block live again without reviving the old local render path.) */
export function scopeMeta(scope: string): { note: string; delegated?: boolean; optional?: boolean } | undefined {
  for (const g of Object.values(SHARED_CREDENTIAL_GROUPS)) {
    const m = g.scopeMeta[scope];
    if (m) return m;
  }
  return undefined;
}

export const INTEGRATION_CONFIGS: Record<string, IntegConfig> = { // ui-config — integration provider catalog (field defs); tenant-agnostic
  salesforce: {
    name: "Salesforce", brand: "#00A1E0", icon: "SF",
    auth: "OAuth 2.0 · Connected App",
    desc: "Push new guests to Salesforce as Leads or Contacts, and keep eight core fields (Status, name, email, position, company, title, telephone) in sync on every booking change.",
    inputs: [
      t("Salesforce instance URL", "https://example.my.salesforce.com", true, "https://example.my.salesforce.com"),
      t("Consumer Key (client_id)", "3MVG9...", true, "3MVG9wt4IL4O4Wq...8sZsQp"),
      t("Consumer Secret", "Created in Setup → Apps → App Manager", true, "••••••••••••••••", "password"),
      t("Default record owner", "Pick a Salesforce user", true, "Lori Owens (lori@acme.studio)", "select"),
    ],
    permissions: ["api", "refresh_token", "offline_access", "manage_user_data"],
    docs: "Set up a Salesforce Connected App in Setup → App Manager → New Connected App. Select OAuth scopes 'api' and 'refresh_token, offline_access'. The callback URL is auto-filled below.",
    callback: "https://app.jrni.com/integrations/salesforce/callback",
    guestSync: {
      mode: "lead",
      options: [
        { id: "off", label: "Don't send to Salesforce", sub: "Guests stay in Events in Studio only. No record is created or updated in Salesforce." },
        { id: "lead", label: "Create as Lead", sub: "New guests land in the Salesforce Leads queue, owned by the default record owner. Best for top-of-funnel events.", recommended: true },
        { id: "contact", label: "Create as Contact", sub: "New guests are matched on email and upserted to Contact records. Best when guests are already CRM contacts." },
      ],
      dedupe: "Match on email · update existing if found",
      onUpdate: "Re-sync mapped fields on every booking change (status, check-in, no-show, refund).",
    },
    fieldMap: [
      { jrni: "Status", sfLead: "Status", sfContact: "Events_Status__c", required: true, notes: "Mapped to your Lead Status picklist on Lead, custom field on Contact." },
      { jrni: "First name", sfLead: "FirstName", sfContact: "FirstName", required: true, notes: "" },
      { jrni: "Last name", sfLead: "LastName", sfContact: "LastName", required: true, notes: "" },
      { jrni: "Email", sfLead: "Email", sfContact: "Email", required: true, notes: "Used as the dedupe key." },
      { jrni: "Position", sfLead: "LeadSource", sfContact: "Events_Position__c", required: false, notes: "Role / seniority captured from the booking form." },
      { jrni: "Company", sfLead: "Company", sfContact: "Account.Name", required: true, notes: "Required on Lead; resolves to Contact's parent Account." },
      { jrni: "Title", sfLead: "Title", sfContact: "Title", required: false, notes: "Job title — free text." },
      { jrni: "Telephone", sfLead: "Phone", sfContact: "Phone", required: false, notes: "E.164 formatted on send." },
    ],
    activity: [], // GM10 pushback sweep (2026-07-22): fabricated activity metrics removed — rows come from a contracted activity-log op when one exists
  },
  hubspot: {
    name: "HubSpot", brand: "#FF7A59", icon: "H",
    auth: "Private App Token",
    desc: "Capture event registrations as HubSpot contacts and trigger workflows when guests check in.",
    inputs: [
      t("HubSpot Private App Token", "pat-na1-...", true, "pat-na1-7c4f••••••", "password"),
      t("Default Lifecycle Stage", "", true, "Marketing Qualified Lead", "select"),
      t("Target list", "Pick a HubSpot list", true, "Events · 2026 attendees", "select"),
    ],
    permissions: ["crm.objects.contacts.read", "crm.objects.contacts.write", "crm.lists.write"],
    docs: "Create a Private App in HubSpot Settings → Integrations → Private Apps. Required scopes: crm.objects.contacts.read/write and crm.lists.write.",
    activity: [], // GM10 pushback sweep (2026-07-22): fabricated activity metrics removed — rows come from a contracted activity-log op when one exists
  },
  ga: {
    name: "Google Analytics", brand: "#E37400", icon: "G",
    auth: "GA4 · Measurement Protocol",
    desc: "Send registration and check-in events to your GA4 property so landing-page conversions appear in your attribution model.",
    inputs: [
      t("Measurement ID", "G-XXXXXXXXXX", true, "G-9K42VPL21F"),
      t("API secret", "Create in Admin → Data Streams → Measurement Protocol", true, "••••••••••••", "password"),
      t("Send event names", "registered,checked_in,refunded", false, "registered, checked_in, refunded, unsubscribed"),
    ],
    permissions: ["analytics.write"],
    docs: "Generate an API secret from your GA4 Data Stream → Measurement Protocol section. Events arrive within ~1 hour of being sent.",
    activity: [], // GM10 pushback sweep (2026-07-22): fabricated activity metrics removed — rows come from a contracted activity-log op when one exists
  },
  zoom: {
    name: "Zoom", brand: "#2D8CFF", icon: "Z",
    auth: "OAuth 2.0 · Server-to-Server",
    desc: "Auto-provision a Zoom meeting (or Webinar) for each virtual/hybrid event. Attendees receive their join link in the confirmation email.",
    inputs: [
      t("Account ID", "Find in Zoom Marketplace → Develop → App", true, "abCdEf12GhIjKl34"),
      t("Client ID", "", true, "Zm9xQ2_kWtR8vLp0"),
      t("Client Secret", "", true, "••••••••••••••••", "password"),
      t("Default host", "Pick a Zoom user", true, "events@example.com", "select"),
      t("Provision type", "", true, "Meeting", "select"),
      t("Webinar registration sync", "", false, true, "toggle"),
    ],
    permissions: ["meeting:write:admin", "webinar:write:admin", "user:read:admin"],
    docs: "Create a Server-to-Server OAuth app in the Zoom Marketplace. Add the scopes meeting:write:admin and webinar:write:admin. Paste the three credentials here — JRNI never sees user-level tokens.",
    activity: [], // GM10 pushback sweep (2026-07-22): fabricated activity metrics removed — rows come from a contracted activity-log op when one exists
  },
  webex: {
    name: "Cisco Webex", brand: "#00BD82", icon: "W",
    auth: "OAuth 2.0 · Integration",
    desc: "Auto-create Webex meetings and webinars for virtual events, with join links delivered to registrants.",
    inputs: [
      // F-87 (kit e670c958): `default_host` removed server-side — the authorized
      // account is informational and set by the OAuth connect.
      t("Client ID", "Webex integration client ID", true),
      t("Client Secret", "Webex integration secret", true, "", "password"),
    ],
    permissions: ["meeting:schedules_write", "meeting:participants_read"],
    docs: "Create an integration in the Webex Developer portal with the meeting:schedules_write scope, then Connect to authorize. The authorized account is set by the OAuth connect and shown once connected.",
    callback: "https://app.jrni.com/integrations/webex/callback",
    activity: [],
    needsInput: true,
    missing: "Client ID, Client Secret",
  },
  goto: {
    name: "GoTo Meeting", brand: "#F68D2E", icon: "G",
    auth: "OAuth 2.0",
    desc: "Generate GoTo Meeting links per event and share them with confirmed guests automatically.",
    inputs: [
      // F-87 (kit e670c958): `organizer` removed server-side — the organizer is
      // the OAuth-authorized account.
      t("Client ID", "GoTo developer app client ID", true),
      t("Client Secret", "GoTo developer app secret", true, "", "password"),
    ],
    permissions: ["meetings:write", "identity:read"],
    docs: "Register an OAuth client in the GoTo Developer Center, then Connect to authorize the organizer account whose meetings map to this workspace.",
    callback: "https://app.jrni.com/integrations/goto/callback",
    activity: [],
    needsInput: true,
    missing: "Client ID, Client Secret",
  },
  googlemeet: {
    name: "Google Meet", brand: "#00897B", icon: "M",
    auth: "OAuth 2.0 · Google",
    desc: "Create Google Meet links on event creation and add them to calendar invites and confirmations.",
    inputs: [
      // F-87 (kit e670c958): `google_account` removed server-side (OAuth-owned);
      // `default_calendar` is now optional — blank uses the primary calendar.
      t("Default calendar", "Blank uses the primary calendar", false, "", "select"),
      t("Add Meet link to invites", "", false, true, "toggle"),
    ],
    permissions: ["https://www.googleapis.com/auth/calendar.events"],
    docs: "Meet links are provisioned through Google Calendar — authorize the Google account that owns the calendar, and a Meet link is attached to every event it creates.",
    callback: "https://app.jrni.com/integrations/google-meet/callback",
    activity: [], // GM10 pushback sweep (2026-07-22): fabricated activity metrics removed — rows come from a contracted activity-log op when one exists
  },
  sendgrid: {
    name: "SendGrid", brand: "#1A82E2", icon: "SG",
    auth: "API key",
    desc: "Outbound transactional & marketing email delivery. Configure DNS records to authenticate your sending domain.",
    inputs: [
      t("API key", "SG.xxxxxxxx", true, "SG.5•••••••••••••", "password"),
      t("Sending domain", "events.example.com", true, "events.example.com"),
      t("Default from name", "", true, "Your Company"),
      t("Reply-to address", "", false, "events@example.com"),
      t("Bounce webhook", "Optional · paste your webhook URL", false, "https://app.jrni.com/hooks/sg-bounce"),
    ],
    permissions: ["mail.send", "mail_settings.read", "user.webhooks.event.settings.read"],
    docs: "Create an API key with Restricted Access → Mail Send permission. Add the SPF/DKIM CNAME records SendGrid provides to authenticate your sending domain.",
    dnsRecords: [
      { type: "CNAME", host: "em1234.events.example.com", value: "u123456.wl.sendgrid.net", status: "verified" },
      { type: "CNAME", host: "s1._domainkey.events.example.com", value: "s1.domainkey.u123456.wl.sendgrid.net", status: "verified" },
      { type: "CNAME", host: "s2._domainkey.events.example.com", value: "s2.domainkey.u123456.wl.sendgrid.net", status: "verified" },
      { type: "TXT", host: "_dmarc.events.example.com", value: "v=DMARC1; p=quarantine; rua=mailto:dmarc@acme.studio", status: "warn" },
    ],
    activity: [], // GM10 pushback sweep (2026-07-22): fabricated activity metrics removed — rows come from a contracted activity-log op when one exists
  },
  resend: {
    name: "Resend", brand: "#000000", icon: "R",
    auth: "API key",
    desc: "Developer-first transactional & marketing email with a modern API and React Email templates. Configure DNS records to authenticate your sending domain.",
    inputs: [
      t("API key", "re_xxxxxxxx", true, "re_9•••••••••••••", "password"),
      t("Sending domain", "events.example.com", true, "events.example.com"),
      t("Default from name", "", true, "Your Company"),
      t("Reply-to address", "", false, "events@example.com"),
      t("Broadcast webhook", "Optional · paste your webhook URL", false, "https://app.jrni.com/hooks/resend"),
    ],
    permissions: ["emails.send", "domains.read", "webhooks.read"],
    docs: "Create an API key in the Resend dashboard with full-access or sending permission. Add the SPF/DKIM/DMARC records Resend provides to authenticate your sending domain.",
    dnsRecords: [
      { type: "MX", host: "send.events.example.com", value: "feedback-smtp.us-east-1.amazonses.com", status: "verified" },
      { type: "TXT", host: "send.events.example.com", value: "v=spf1 include:amazonses.com ~all", status: "verified" },
      { type: "TXT", host: "resend._domainkey.events.example.com", value: "p=MIGfMA0GCSqGSIb3DQEBAQUAA4GN…", status: "verified" },
      { type: "TXT", host: "_dmarc.events.example.com", value: "v=DMARC1; p=none; rua=mailto:dmarc@acme.studio", status: "verified" },
    ],
    activity: [], // GM10 pushback sweep (2026-07-22): fabricated activity metrics removed — rows come from a contracted activity-log op when one exists
  },
  twilio: {
    name: "Twilio", brand: "#F22F46", icon: "T",
    auth: "Account SID + Auth Token",
    desc: "SMS delivery for reminders, check-in links, and waitlist promotions. Carrier-grade with delivery receipts.",
    // 2026-08-04: Twilio is served as a shared-credential app registration (one account,
    // Programmable Messaging + Conversations, channels switched individually — seed-world
    // integrations.json + the serving-engine binding). This key only tells the detail page
    // to wait for that record instead of flashing the legacy single-service form; the
    // inputs below stay as the fallback for an environment that can't serve the record.
    sharedCredentials: "twilio-account",
    inputs: [
      t("Account SID", "ACxxxxxxxx", true, "AC8a••••••••••••••"),
      t("Auth Token", "Generated in Twilio Console", true, "••••••••••••••", "password"),
      t("Messaging Service SID", "MGxxxxxxxx (recommended)", true, "MG7d11••••••••••"),
      t("Sender ID", "Alphanumeric or +1 number", false, "JRNI-EVENTS"),
      /* NO WhatsApp field here, deliberately (2026-08-04). The served shared-credential
         registration already declares `whatsapp_sender` (seed-world/integrations.json → Twilio →
         Programmable Messaging → WhatsApp channel → configFields, label "WhatsApp sender",
         required, built by the 0905 registration work) and IntegrationDetail renders that record
         for Twilio, not this list. Adding one here produced a second, disagreeing definition —
         different label, requiredness and format hint — which is exactly the UI-duplicates-a-
         server-catalog drift this file is supposed to stop growing. Reverted. */
      t("Default country code", "+1", false, "+1"),
    ],
    permissions: ["api.messaging.write", "api.account.read"],
    docs: "Get Account SID and Auth Token from your Twilio Console dashboard. We recommend using a Messaging Service to manage sender pools, opt-outs, and compliance automatically.",
    activity: [], // GM10 pushback sweep (2026-07-22): fabricated activity metrics removed — rows come from a contracted activity-log op when one exists
  },
  slack: {
    name: "Slack", brand: "#4A154B", icon: "#",
    auth: "OAuth 2.0",
    desc: "Pipe event alerts (capacity hit, no-shows, sync failures) into a Slack channel. Optional command shortcuts for staff.",
    inputs: [
      t("Workspace", "Click Connect to authorize", true),
      t("Default channel", "Pick a channel after connecting", true, "", "select"),
      t("Alert categories", "", true, "", "mapping"),
    ],
    permissions: ["chat:write", "channels:read", "groups:read", "commands"],
    docs: "Click 'Connect Slack' below to authorize JRNI in your workspace. After authorization you'll pick a channel and choose which alerts to forward.",
    activity: [],
    needsInput: true,
    missing: "Workspace authorization, default channel selection",
  },
  stripe: {
    name: "Stripe", brand: "#635BFF", icon: "S",
    auth: "Connect Standard (OAuth)",
    desc: "Take paid ticket revenue. Connect your Stripe account with one click — funds settle directly to your Stripe balance, with full refund support.",
    // ui-config — provider field-definition catalog (static config, not records)
    inputs: [
      { ...t("Stripe account", "Connect to link your account", true, "", "readonly", "acct_1Nx9••••••"), desc: "Your connected Stripe account ID — provisioned automatically when you connect via Stripe Connect. Not editable here." },
      { ...t("Default currency", "Set automatically after you connect", false, "", "readonly", "USD (from your Stripe account)"), desc: "Read from your connected Stripe account. Change it in your Stripe dashboard, not here." },
      { ...t("Processing fee passthrough", "", false, false, "toggle"), desc: "When on, Stripe's per-transaction processing fee is added to the ticket price so the guest covers it at checkout. When off, the fee is deducted from your payout. Availability varies by region — pending legal review of card-surcharge rules." },
    ],
    permissions: ["read_write"],
    docs: "Click 'Connect Stripe' below to authorize Events in Studio on your Stripe account via Connect Standard (OAuth). The publishable key and webhook signing secret are provisioned automatically — there's nothing to paste. Your default currency is read from the connected account. We register the webhook endpoint below for you, subscribed to payment_intent.succeeded and charge.refunded.",
    callback: "https://app.jrni.com/integrations/stripe/oauth/callback",
    needsInput: true,
    missing: "Connect your Stripe account via OAuth",
    activity: [],
  },
  paypal: {
    name: "PayPal", brand: "#003087", icon: "P",
    auth: "OAuth 2.0 · REST API",
    desc: "Let guests check out with their PayPal balance, Pay Later, or Venmo at registration.",
    // REQ-045 (2026-07-20, merged 2026-07-23): PayPal is CONNECT-based like
    // Stripe — OAuth partner onboarding via the REQ-045e connect ops. No pasted
    // Client-ID/Secret/Webhook fields: credentials, webhooks and the merchant
    // grant are provisioned by PayPal during onboarding; checkout is enabled
    // only once PayPal confirms the account can RECEIVE payments ("verified").
    // ui-config — provider field-definition catalog (static config, not records)
    inputs: [
      { ...t("PayPal merchant", "Filled by PayPal onboarding", true, "", "mapping"), desc: "Your PayPal merchant account — filled automatically when PayPal onboarding completes. Not editable here." },
      { ...t("Default currency", "Set during PayPal onboarding", false, "", "mapping"), desc: "Set from your PayPal account during onboarding. Not editable here." },
    ],
    permissions: ["openid", "https://uri.paypal.com/services/payments/payment"],
    docs: "Click Connect with PayPal and finish onboarding in the PayPal window — there are no API keys to paste. Webhooks are provisioned automatically. Checkout stays disabled until PayPal confirms the account can receive payments.",
    activity: [],
  },
  anet: {
    name: "Authorize.net", brand: "#E0792B", icon: "A",
    auth: "API login ID · Transaction key",
    desc: "Process card payments and eChecks for paid tickets through the Authorize.net gateway. Supports hosted Accept payment forms, refunds and automatic settlement reconciliation.",
    // ui-config — provider field-definition catalog (static config, not records)
    inputs: [
      t("API Login ID", "Authorize.net API login ID", true),
      t("Transaction Key", "16-character transaction key", true, "", "password"),
      t("Public Client Key", "For Accept.js hosted forms", false),
      t("Signature Key", "Webhook signature (HMAC-SHA512)", true, "", "password"),
      t("Environment", "", true, "", "select"),
      t("Default currency", "", true, "", "select"),
    ],
    permissions: ["transactions.charge", "transactions.refund", "webhooks.subscribe"],
    docs: "In the Authorize.net Merchant Interface, generate an API Login ID and Transaction Key under Account → Security Settings. For hosted Accept.js forms add a Public Client Key, and create a Signature Key to verify webhooks. Point webhooks at the URL below subscribed to net.authorize.payment.authcapture.created and .refund.created.",
    callback: "https://app.jrni.com/integrations/authorize-net/webhooks",
    activity: [],
    needsInput: true,
    missing: "API Login ID, Transaction Key, Signature Key, environment, default currency",
  },
  adyen: {
    name: "Adyen", brand: "#0ABF53", icon: "A",
    auth: "Hosted onboarding · Sessions API",
    desc: "Enterprise processing with 100+ local payment methods and unified global settlement.",
    // REQ-060 (2026-07-23): Adyen is CONNECT-based like Stripe/PayPal — hosted
    // onboarding via startAdyenOnboarding. No pasted API key / Merchant account /
    // Client key / HMAC fields: those are PLATFORM secrets held by the backend,
    // never entered per tenant. Checkout goes live only once Adyen finishes
    // KYC/verification ("verified" from getAdyenConnectionStatus).
    // ui-config — provider field-definition catalog (static config, not records)
    inputs: [
      { ...t("Environment", "Set by Adyen onboarding", false, "", "mapping"), desc: "Test or live — set by Adyen hosted onboarding. The platform controls it; not editable here." },
    ],
    permissions: ["Checkout Sessions", "Hosted Onboarding"],
    docs: "Click Connect with Adyen and finish the hosted onboarding in the Adyen window — there are no API keys to paste. Webhooks and credentials are provisioned by the platform. Checkout stays disabled until Adyen finishes verifying the account.",
    activity: [],
  },
  square: {
    name: "Square", brand: "#006AFF", icon: "□",
    auth: "OAuth 2.0",
    desc: "Reconcile online ticket sales with in-person Square POS for box-office and walk-ups.",
    inputs: [
      t("Application ID", "sq0idp-...", true),
      t("Access token", "Production access token", true, "", "password"),
      t("Location", "", true, "", "select"),
    ],
    permissions: ["PAYMENTS_WRITE", "ORDERS_WRITE", "MERCHANT_PROFILE_READ"],
    docs: "Create an application in the Square Developer dashboard, authorize via OAuth, and pick the location whose payments map to this workspace.",
    callback: "https://app.jrni.com/integrations/square/callback",
    activity: [],
    needsInput: true,
    missing: "Application ID, Access token, Location",
  },
  braintree: {
    name: "Braintree", brand: "#000000", icon: "B",
    auth: "API credentials",
    desc: "PayPal-owned gateway for cards and digital wallets with built-in fraud protection.",
    inputs: [
      t("Merchant ID", "Braintree merchant ID", true),
      t("Public key", "Public key", true),
      t("Private key", "Private key", true, "", "password"),
      t("Environment", "", true, "", "select"),
    ],
    permissions: ["transaction:sale", "transaction:refund"],
    docs: "Find your merchant ID, public and private keys under Settings → API in the Braintree control panel. Choose the Production environment when you go live.",
    activity: [],
    needsInput: true,
    missing: "Merchant ID, Public key, Private key, Environment",
  },
  razorpay: {
    name: "Razorpay", brand: "#0C2451", icon: "R",
    auth: "Key ID + Key secret",
    desc: "Accept UPI, cards and netbanking for events across India and APAC markets.",
    inputs: [
      t("Key ID", "rzp_live_...", true),
      t("Key secret", "Live key secret", true, "", "password"),
      t("Webhook secret", "Webhook signing secret", true, "", "password"),
    ],
    permissions: ["payments", "refunds"],
    docs: "Generate live API keys under Settings → API Keys in the Razorpay dashboard, then add a webhook to the endpoint below for payment.captured and refund.processed.",
    callback: "https://app.jrni.com/integrations/razorpay/webhooks",
    activity: [],
    needsInput: true,
    missing: "Key ID, Key secret, Webhook secret",
  },
  rwg: {
    name: "Reserve with Google", brand: "#4285F4", icon: "R",
    auth: "OAuth 2.0 · Google Business Profile",
    desc: "List your bookable events and appointments on Google Search and Maps so guests can reserve directly from Google. Availability, capacity and cancellations sync back to Events in Studio in real time.",
    inputs: [
      t("Google Business Profile", "Connected via OAuth", true, "Your Business · Boston"),
      t("Merchant / Partner ID", "Reserve with Google partner ID", true, "rwg-partner-880412"),
      t("Service area / location", "Pick a Business Profile location", true, "Boston · Seaport flagship", "select"),
      t("Bookable inventory", "", true, "Public events + appointments", "select"),
      t("Confirmation channel", "", false, "Email + SMS", "select"),
    ],
    permissions: ["business.manage", "maps.reserve.partners", "offline_access"],
    docs: "Connect the Google Business Profile that owns your locations, then map which inventory (events, appointments, or both) is published as bookable. Reservations made on Google create a guest record and decrement capacity here; cancellations sync both ways.",
    callback: "https://app.jrni.com/integrations/reserve-with-google/callback",
    activity: [],
    needsInput: true,
    missing: "Business Profile, partner ID, location, inventory",
  },
  gmaps: {
    name: "Google Maps", brand: "#34A853", icon: "G",
    auth: "API key · Google Cloud",
    desc: "Use Google Maps to render location maps, place details and directions wherever an event, appointment or queue location is shown — landing pages, confirmation emails, and the check-in kiosk.",
    inputs: [
      t("Maps JavaScript API key", "AIza...", true, "AIza••••••••••••••••", "password"),
      t("Set as default map provider", "", true, true, "toggle"),
      t("Default map style", "", false, "Brand · light", "select"),
      t("Show directions link", "", false, true, "toggle"),
      t("Show Street View", "", false, false, "toggle"),
    ],
    permissions: ["Maps JavaScript API", "Places API", "Geocoding API"],
    docs: "Create an API key in Google Cloud with the Maps JavaScript, Places and Geocoding APIs enabled, then restrict it to your domains. When set as default, every location map across events, appointments and queues renders with Google Maps.",
    callback: "https://app.jrni.com/integrations/google-maps/callback",
    activity: [], // GM10 pushback sweep (2026-07-22): fabricated activity metrics removed — rows come from a contracted activity-log op when one exists
  },
  amaps: {
    name: "Apple Maps", brand: "#34C759", icon: "",
    auth: "MapKit JS · Apple Developer token",
    desc: "Use Apple Maps (MapKit JS) to render location maps and directions — the most native experience for guests on iPhone, iPad and Mac. Choose it as the default map provider, or let Apple devices fall back to it automatically.",
    inputs: [
      t("Apple Developer Team ID", "10-character Team ID", true),
      t("MapKit Key ID", "Key ID from your MapKit JS key", true),
      t("MapKit private key (.p8)", "Upload the .p8 key file", true, "", "password"),
      t("Set as default map provider", "", false, false, "toggle"),
      t("Use on Apple devices automatically", "", false, true, "toggle"),
    ],
    permissions: ["MapKit JS", "Maps Web Snapshots"],
    docs: "In your Apple Developer account, create a MapKit JS key and note the Team ID and Key ID. Upload the .p8 private key here to mint map tokens. You can set Apple Maps as the global default, or have it serve automatically only to guests on Apple devices while another provider handles the rest.",
    activity: [],
    needsInput: true,
    missing: "Team ID, Key ID, MapKit private key",
  },
  "google-cal": {
    name: "Google Calendar", brand: "#1A73E8", icon: "G",
    auth: "OAuth 2.0 · Google",
    desc: "Two-way sync between events and Google Calendar — changes on either side stay in step, with automatic guest invites.",
    inputs: [
      t("Google account", "Connected via OAuth", true, "events@example.com"),
      t("Target calendar", "", true, "Your Company", "select"),
      t("Sync direction", "", true, "Two-way", "select"),
      t("Send guest invites", "", false, true, "toggle"),
    ],
    permissions: ["https://www.googleapis.com/auth/calendar.events"],
    docs: "Authorize with the Google account that owns the calendar, then choose which calendar receives events and whether sync is one- or two-way.",
    callback: "https://app.jrni.com/integrations/google-calendar/callback",
    activity: [], // GM10 pushback sweep (2026-07-22): fabricated activity metrics removed — rows come from a contracted activity-log op when one exists
  },
  outlook: {
    name: "Microsoft 365 Calendar", brand: "#D83B01", icon: "O",
    auth: "OAuth 2.0 · Azure AD",
    desc: "Two-way sync of events with the Microsoft 365 Calendar on work and school accounts.",
    sharedCredentials: "ms-app",
    inputs: [],
    permissions: ["Calendars.ReadWrite"],
    docs: "Calendar sync runs on the shared Microsoft app registration above — grant it the Microsoft Graph 'Calendars.ReadWrite' permission.",
    callback: "https://app.jrni.com/integrations/m365/callback",
    activity: [], // GM10 pushback sweep (2026-07-22): fabricated activity metrics removed — rows come from a contracted activity-log op when one exists
  },
  // Apple Wallet passes. Present so IntegrationDetail's
  // `INTEGRATION_CONFIGS[id] || INTEGRATION_CONFIGS.salesforce` fallback never resolves this
  // registration to SALESFORCE while the backend record is still loading — which rendered
  // Salesforce chrome (title, fields, callback URL) on the Apple page for a beat on every visit.
  // `sharedCredentials` tells the detail page to wait for the served registration instead of
  // flashing the legacy single-service form; the real fields come from the backend.
  apple: {
    name: "Apple Pass", brand: "#000000", icon: "",
    auth: "Pass Type ID certificate",
    desc: "Apple Wallet passes for tickets, appointments and queue positions — signed with your Pass Type ID certificate.",
    sharedCredentials: "apple-wallet",
    inputs: [],
    // MUST stay empty. Two scope strings ("PassKit · pass signing", "PassKit web
    // service · pass updates") were invented here and RENDERED to real admins as
    // ticked, granted permissions during the fetch window. They exist in no
    // contract, no mock and no backend. Apple consents nothing on this
    // registration — no API is called on anyone's behalf, so no scope exists to
    // list. Empty is the truthful value and matches what the data layer serves.
    // Same rule for `inputs` and `activity`: never plausible-looking placeholder
    // content — if there is no real value, leave it empty.
    permissions: [],
    docs: "In your Apple Developer account, create a Pass Type ID and export its certificate from Keychain Access as a .p12. Upload it with the pass images — passes are signed per company; nothing is shared across tenants.",
    activity: [],
  },
  icloud: {
    name: "Apple iCloud", brand: "#3693F3", icon: "",
    auth: "App-specific password · CalDAV",
    desc: "Two-way sync with iCloud Calendar so events stay current in Apple Calendar on Mac, iPhone and iPad.",
    inputs: [
      t("Apple ID", "name@icloud.com", true),
      t("App-specific password", "Generated at appleid.apple.com", true, "", "password"),
      t("Target calendar", "", true, "", "select"),
    ],
    permissions: ["CalDAV read/write"],
    docs: "Generate an app-specific password in your Apple ID security settings (two-factor required), then sign in with your Apple ID to publish events over CalDAV.",
    activity: [],
    needsInput: true,
    missing: "Apple ID, app-specific password, target calendar",
  },
  caldav: {
    name: "CalDAV", brand: "#5B6472", icon: "C",
    auth: "CalDAV · Basic auth",
    desc: "Two-way sync with any CalDAV-compatible calendar via a standards-based feed.",
    inputs: [
      t("Server URL", "https://dav.example.com/calendars/", true),
      t("Username", "Calendar account username", true),
      t("Password", "Account or app password", true, "", "password"),
    ],
    permissions: ["read/write"],
    docs: "Enter your provider's CalDAV server URL and credentials. Most providers expose a principal or calendar-collection URL you can paste here.",
    activity: [],
    needsInput: true,
    missing: "Server URL, username, password",
  },
  dynamics: {
    name: "Microsoft Dynamics", brand: "#0078D4", icon: "D",
    auth: "OAuth 2.0 · Azure AD",
    desc: "Two-way sync of contacts, leads, accounts, and event activity with Microsoft Dynamics 365 Sales / Customer Service.",
    inputs: [
      t("Tenant ID", "Azure AD directory (tenant) ID", true),
      t("Application (Client) ID", "Registered Azure AD app ID", true),
      t("Client Secret", "Azure AD app secret", true, "", "password"),
      t("Dynamics environment URL", "https://acme.crm.dynamics.com", true),
      t("Default sync entity", "", true, "", "select"),
      t("Field mapping", "", true, "", "mapping"),
    ],
    permissions: ["Dynamics CRM user_impersonation", "offline_access"],
    docs: "Register an Azure AD app in your tenant with API permission 'Dynamics CRM user_impersonation' and a delegated permission grant. Paste the tenant, client ID, secret, and your Dynamics environment URL.",
    callback: "https://app.jrni.com/integrations/dynamics/callback",
    activity: [],
    needsInput: true,
    missing: "Tenant ID, Client ID, Client Secret, environment URL, field mapping",
  },
  entra: {
    name: "Microsoft Entra ID", brand: "#0078D4", icon: "E",
    auth: "OAuth 2.0 · Azure AD",
    desc: "Workspace sign-in and user management through Microsoft Entra ID — single sign-on for staff and automatic directory sync.",
    sharedCredentials: "ms-app",
    inputs: [],
    permissions: ["User.Read", "User.Read.All", "GroupMember.Read.All"],
    docs: "Runs on the shared Microsoft app registration above — the delegated 'User.Read' permission covers sign-in; directory features use application permissions granted with admin consent.",
    callback: "https://app.jrni.com/integrations/entra/callback",
    activity: [],
  },
  exchange: {
    name: "Microsoft Exchange", brand: "#0F6CBD", icon: "E",
    auth: "OAuth 2.0 · Azure AD (O365)",
    desc: "Two-way calendar sync with Microsoft Exchange Online. Push event blocks to staff calendars and pull busy/free availability to prevent double-booking.",
    inputs: [
      t("Tenant ID", "Azure AD directory ID", true),
      t("Application (Client) ID", "", true),
      t("Client Secret", "", true, "", "password"),
      t("Default mailbox / room list", "rooms@acme.studio", true),
      t("Sync direction", "", true, "Two-way (busy/free + push)", "select"),
      t("Conflict resolution", "", true, "Exchange wins on overlap", "select"),
    ],
    permissions: ["Calendars.ReadWrite", "Place.Read.All", "User.Read.All", "offline_access"],
    docs: "Register an Azure AD app for your O365 tenant with delegated Calendars.ReadWrite. For room/resource sync, also grant Place.Read.All. Use the default mailbox or room list to limit what JRNI sees.",
    callback: "https://app.jrni.com/integrations/exchange/callback",
    activity: [],
    needsInput: true,
    missing: "Tenant ID, Client ID, Client Secret, default mailbox / room list",
  },
  teams: {
    name: "Microsoft Teams", brand: "#5059C9", icon: "T",
    auth: "OAuth 2.0 · Azure AD",
    desc: "Auto-provision Teams meetings for hybrid and virtual events. Sends join links to virtual attendees and routes Q&A questions to a dedicated channel.",
    sharedCredentials: "ms-app",
    inputs: [
      // F-87 (kit e670c958): `default_organizer` removed server-side — meetings
      // are created as the OAuth-authorized account.
      t("Provision type", "", true, "Meeting", "select"),
      t("Q&A channel", "Optional", false),
    ],
    permissions: ["OnlineMeetings.ReadWrite.All"],
    docs: "Runs on the shared Microsoft app registration above — grant it OnlineMeetings.ReadWrite.All to create Teams meetings on behalf of users.",
    activity: [],
  },
  /* Ellucian Ethos (Banner SIS) — CD brief 2026-08-19-0002, overlay kit 7cc9e00e.
   * Present for the same reason `apple` is: without an entry here
   * `INTEGRATION_CONFIGS[id] || INTEGRATION_CONFIGS.salesforce` resolves the Ethos
   * page to SALESFORCE while the served record loads, flashing Salesforce chrome
   * (and its guest-sync/field-map panels) on a student-records page.
   *
   * `inputs` is EMPTY on purpose. The two real fields (base_url, api_key) come off
   * the served record — with the backend's own labels, placeholders, descriptions
   * and the `masked`/`configured` facts the write-only credential input keys off —
   * and EthosIntegration.tsx renders them. A second copy here would be a field
   * definition the UI invented, which is exactly the drift the Twilio note above
   * refuses to keep growing. Same for `permissions`: the six required resource
   * grants arrive as the record's `scopes`, and a local copy could disagree with
   * the list a failed preflight names. */
  ethos: {
    name: "Ellucian Ethos (Banner SIS)", brand: "#6244BB", icon: "E",
    auth: "API key",
    desc: "Read-only student-information lane into Banner: resolve the signed-in student to their SIS record at SSO login, then read an eligibility snapshot — academic level, program, holds — at booking, queue-join and registration time. Never writes to Banner.",
    inputs: [],
    permissions: [],
    docs: "Create an application in Ethos Integration, grant it every resource this lane reads, then paste that application's API key and your region's Ethos Integration base URL. There is no OAuth step — the credential authenticates the application, not a person.",
    activity: [],
  },
  zapier: {
    name: "Zapier", brand: "#FF4A00", icon: "Z",
    auth: "API key",
    desc: "Connect Events in Studio to 5,000+ apps via Zaps. Trigger on event published, registration, check-in, and waitlist movements. Or use Actions to create events, add guests, and send invites from other tools.",
    inputs: [
      t("Zapier API key", "Generated from your JRNI account → Developer", true, "", "password"),
      t("Webhook URL", "Paste your Zapier webhook target", false),
      t("Default trigger events", "", false, "", "mapping"),
    ],
    permissions: ["events.read", "events.write", "guests.read", "guests.write", "campaigns.read"],
    docs: "Generate an API key from your JRNI account → Developer. Use it in Zapier when adding the JRNI app. Zaps can fire on registration, check-in, capacity hit, or any custom event — and JRNI Actions can be triggered from anywhere a Zap runs.",
    callback: "https://hooks.zapier.com/hooks/standard/...",
    activity: [],
    needsInput: true,
    missing: "API key (generate one to start), then configure your first Zap in Zapier",
  },
};

/* ── Shared connection state (list + detail) ─────────────────────────────
 * 2026-07-03 (Claude Code push-back, §2): `updateIntegrationConfig` (PATCH
 * /v1/admin/integrations/{provider}/config) is live over a dedicated
 * workers/integrations service. loadConnections still returns synchronously
 * (many call sites read it once on mount) — it now hydrates its in-memory
 * cache from `listIntegrationDetails` in the background and reconciles via
 * a subscriber, while saveConnections persists each toggle through
 * `updateIntegrationConfig`. localStorage is kept only as the same-session
 * fallback for when the client operations aren't present. */
import { listIntegrationDetails, updateIntegrationConfig } from "@v0y4ge/api-client";
import { isSignedIn, can, onTenantChange, onTenantSwitching } from "../../shell/access";

const CONN_KEY = "esm.integrations.connections.v1";
// §3a (2026-07-19): no integrations are assumed connected — the old DEFAULT_ON
// fabricated four enabled tenant connections before real state loaded.
const _connSubs = new Set<() => void>();
let _connCache: Record<string, boolean> | null = null;

/* F-87 (kit e670c958) §6/§7 — the UI catalog keeps its historical ids; the ops
 * speak the backend provider ids. Only the divergent ones are mapped (the
 * api-client's normalizeIntegrationProvider covers its own alias set). */
export const BACKEND_PROVIDER_IDS: Record<string, string> = { teams: "msteams", googlemeet: "gmeet", "google-cal": "google_calendar", ga: "ga4" };
export const backendProviderId = (uiId: string): string => BACKEND_PROVIDER_IDS[uiId] || uiId;

export interface ConnectionRow {
  provider: string; enabled: boolean;
  status?: string; configuration_status?: string; last_error?: string | null;
  /** Shared-registration deep-link (kit 735eab65): a member service's card opens the ONE shared app-registration page, featureKey pre-focused. */
  registrationId?: string; featureKey?: string;
}
let _rowCache: Record<string, ConnectionRow> | null = null;

let _connHydrated = false;
async function hydrateConnections() {
  // §2 boot hygiene (2026-07-19) + role gate (UIR-25, Voyage #182 2026-07-25):
  // imported at bundle load via screens.tsx — never fetch signed-out, and never
  // fire the ADMIN-scoped listIntegrationDetails from a non-admin session (it
  // draws a live 403). Skip keeps the flag clear for re-invoke on subscribe/load
  // once an admin session exists.
  if (_connHydrated || !isSignedIn() || !can("settings.read")) return;
  _connHydrated = true;
  try {
    if (typeof listIntegrationDetails !== "function") return;
    const r: any = await listIntegrationDetails({});
    const rows: any[] = r?.data ?? r?.integrations ?? [];
    if (Array.isArray(rows) && rows.length) {
      const next: Record<string, boolean> = {};
      const nextRows: Record<string, ConnectionRow> = {};
      rows.forEach((row) => {
        if (!row?.provider) return;
        next[row.provider] = !!(row.enabled ?? row.on);
        nextRows[row.provider] = {
          provider: String(row.provider),
          enabled: !!(row.enabled ?? row.on),
          status: (row.status ?? row.connection?.status) as string | undefined,
          configuration_status: (row.configuration_status ?? row.connection?.configuration_status) as string | undefined,
          last_error: (row.connection?.last_error ?? row.last_error ?? null) as string | null,
          registrationId: typeof row.registrationId === "string" ? row.registrationId : undefined,
          featureKey: typeof row.featureKey === "string" ? row.featureKey : undefined,
        };
      });
      _connCache = next;
      _rowCache = nextRows;
      _connSubs.forEach((fn) => fn());
    }
  } catch (e) { console.warn("listIntegrationDetails hydrate failed, keeping local fallback:", e); }
}
hydrateConnections(); // no-op until an admin session exists (§2 + UIR-25)
// Cross-tenant leak fix (docket 2026-07-27, item 1): purge SYNCHRONOUSLY the
// instant a switch starts (`_connHydrated = true` BLOCKS an incidental
// hydrateConnections() call during the pending window); (re)hydrate only
// once the tenant is CONFIRMED.
onTenantSwitching(() => { _connCache = null; _rowCache = null; _connHydrated = true; _connSubs.forEach((fn) => fn()); });
onTenantChange(() => { _connHydrated = false; void hydrateConnections(); });

export function subscribeConnections(fn: () => void) { hydrateConnections(); _connSubs.add(fn); return () => { _connSubs.delete(fn); }; }

export function loadConnectionRows(): Record<string, ConnectionRow> { hydrateConnections(); return _rowCache || {}; }

/* Re-fetch server connection state after a mutating integration op resolves. */
export function refreshConnections(): Promise<void> { _connHydrated = false; return hydrateConnections(); }

/* §6 honest states (kit e670c958) — catalog/detail rows never claim Connected
 * without a real verify: `configured` + `needs-attention` renders "Configured —
 * not verified" with a verify CTA; configuration_status `error` = credentials
 * undecryptable (key rotation) → surface connection.last_error, prompt re-entry
 * (PATCHing secrets in that state 409s PROVIDER_SECRETS_UNREADABLE). */
export type IntegStatusKind = "connected" | "configured-unverified" | "needs-attention" | "error" | "disconnected";
export function integrationStatus(uiId: string): { kind: IntegStatusKind; lastError?: string | null } {
  const row = loadConnectionRows()[backendProviderId(uiId)];
  if (!row) return { kind: "disconnected" };
  if (row.configuration_status === "error") return { kind: "error", lastError: row.last_error };
  if (row.status === "connected") return { kind: "connected" };
  if (row.status === "disconnected" || row.configuration_status === "disabled") return { kind: "disconnected" };
  if (row.configuration_status === "configured") return { kind: "configured-unverified" };
  return { kind: "needs-attention" };
}

export function loadConnections(): Record<string, boolean> {
  hydrateConnections();
  if (_connCache) return _connCache;
  try {
    const raw = localStorage.getItem(CONN_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {}; // nothing connected until real state hydrates (§3a)
}
export function saveConnections(c: Record<string, boolean>) {
  const prev = _connCache ?? loadConnections();
  _connCache = c;
  try { localStorage.setItem(CONN_KEY, JSON.stringify(c)); } catch { /* ignore */ }
  if (typeof updateIntegrationConfig === "function") {
    Object.keys(c).forEach((provider) => {
      if (c[provider] !== prev[provider]) {
        updateIntegrationConfig(provider, { enabled: c[provider] }).catch((e: unknown) => console.warn(`updateIntegrationConfig(${provider}) failed:`, e));
      }
    });
  }
}


/* ───────────────────────────────────────────────────────────────────────────
 * Wallet pass appearance (JAI-581) — shared between the Appearance group in
 * AppRegistration and the pass preview, so the ratio the operator is warned
 * about is measured against the colours the card actually paints. Lives here
 * rather than in either screen because both import this module already and a
 * value import between the two would be a cycle.
 * ─────────────────────────────────────────────────────────────────────────── */

/** The three tenant-default colour keys, in the order the group renders them. */
export const PASS_COLOR_KEYS = ["pass_background_color", "pass_foreground_color", "pass_label_color"] as const;
export type PassColorKey = (typeof PASS_COLOR_KEYS)[number];

/** The image-asset key — NOT a settings field; it uploads through the credential route. */
export const PASS_BACKGROUND_IMAGE_KEY = "pass_background_image";

/** Strict #RRGGBB. The backend accepts nothing else and normalises nothing. */
export const PASS_HEX_RE = /^#[0-9A-Fa-f]{6}$/;
export function isPassHex(v: unknown): v is string { return typeof v === "string" && PASS_HEX_RE.test(v); }

/** Platform defaults per pass style — what the preview painted before JAI-581,
 *  now the unset case. Only `eventTicket` corresponds to a pass that is issued
 *  today; appointment and queue are message-authoring template shapes (JAI-125). */
export interface PassStyleDefault { id: "eventTicket" | "appointment" | "queue"; bg: string; fg: string; issued: boolean }
export const PASS_STYLE_DEFAULTS: PassStyleDefault[] = [
  { id: "eventTicket", bg: "#1F3A5F", fg: "#FFFFFF", issued: true },
  { id: "appointment", bg: "#FFFFFF", fg: "#101828", issued: false },
  { id: "queue", bg: "#0F3D2E", fg: "#FFFFFF", issued: false },
];

/** Opacity Wallet field LABELS render at when no label colour is configured. */
export const PASS_LABEL_DEFAULT_ALPHA = 0.62;

export interface PassAppearance { pass_background_color: string | null; pass_foreground_color: string | null; pass_label_color: string | null }

/** Read the three stored values off a feature's configFields. Anything that is
 *  not strict #RRGGBB (absent, "", a legacy value) reads as null — unset. */
export function readPassAppearance(configFields: unknown): PassAppearance {
  const out: PassAppearance = { pass_background_color: null, pass_foreground_color: null, pass_label_color: null };
  if (!Array.isArray(configFields)) return out;
  configFields.forEach((raw) => {
    const cf = raw as Record<string, unknown>;
    const key = String(cf.key ?? "");
    if ((PASS_COLOR_KEYS as readonly string[]).indexOf(key) < 0) return;
    const v = typeof cf.filled === "string" && cf.filled ? cf.filled : typeof cf.value === "string" ? cf.value : "";
    if (isPassHex(v)) out[key as PassColorKey] = v.toUpperCase();
  });
  return out;
}

function hexRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function toHex(r: number, g: number, b: number) {
  const c = (n: number) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, "0").toUpperCase();
  return `#${c(r)}${c(g)}${c(b)}`;
}
/** Flatten `over` at `alpha` onto `under` — the label default is translucent
 *  text, and a ratio measured against a translucent colour is meaningless. */
export function compositeHex(over: string, under: string, alpha: number): string {
  const [r1, g1, b1] = hexRgb(over), [r2, g2, b2] = hexRgb(under);
  return toHex(r1 * alpha + r2 * (1 - alpha), g1 * alpha + g2 * (1 - alpha), b1 * alpha + b2 * (1 - alpha));
}
function luminance(hex: string) {
  const lin = (c: number) => { const cs = c / 255; return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4); };
  const [r, g, b] = hexRgb(hex);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
export function passContrast(a: string, b: string): number {
  const la = luminance(a), lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export interface ResolvedPassColors { bg: string; fg: string; label: string; labelAlpha: number; ratio: number }
/** What the card PAINTS for one style, given the tenant's (partial) settings.
 *  A configured value replaces the platform default on every style — one colour
 *  set applies to all three. `label` is the resolved, already-composited colour
 *  the ratio is measured against. */
export function resolvePassColors(style: PassStyleDefault, a: PassAppearance): ResolvedPassColors {
  const bg = a.pass_background_color ?? style.bg;
  const fg = a.pass_foreground_color ?? style.fg;
  const labelAlpha = a.pass_label_color ? 1 : PASS_LABEL_DEFAULT_ALPHA;
  const label = a.pass_label_color ?? compositeHex(fg, bg, PASS_LABEL_DEFAULT_ALPHA);
  return { bg, fg, label, labelAlpha, ratio: passContrast(label, bg) };
}

/** WCAG AA for normal text. Advisory only — never a save blocker, because the
 *  backend does not validate contrast and never will: a blocking client check
 *  would enforce a rule production does not, splitting design time from
 *  production on what is acceptable. */
export const PASS_CONTRAST_MIN = 4.5;

export type PassContrastNotice =
  | { kind: "defect"; ratio: number }
  | { kind: "attribution"; styleId: PassStyleDefault["id"]; ratio: number }
  | null;

/** Aggregate across ALL THREE styles, so an operator is not made to click each
 *  tab to discover a problem — and split by what the finding MEANS:
 *
 *  - The event ticket below threshold is a real defect. That pass is issued, so
 *    a guest can genuinely receive an unreadable one; name the ratio.
 *  - Divergence on appointment/queue is caused by the LABEL COLOUR BEING UNSET,
 *    not by a property of those passes — it disappears the moment all three are
 *    set. Naming them as defects would send an operator to fix a pass nobody can
 *    receive, by distorting a background. Attribute it to the cause instead.
 *
 *  When issuance for those shapes lands (JAI-125), the attribution note becomes
 *  a defect warning with no rework: all three pairings are already evaluated. */
export function passContrastNotice(a: PassAppearance): PassContrastNotice {
  const resolved = PASS_STYLE_DEFAULTS.map((s) => ({ s, r: resolvePassColors(s, a) }));
  const issued = resolved.find((x) => x.s.issued);
  if (issued && issued.r.ratio < PASS_CONTRAST_MIN) return { kind: "defect", ratio: issued.r.ratio };
  if (a.pass_label_color) return null; // all three share one label colour — no divergence left to attribute
  const worst = resolved.filter((x) => !x.s.issued && x.r.ratio < PASS_CONTRAST_MIN).sort((x, y) => x.r.ratio - y.r.ratio)[0];
  return worst ? { kind: "attribution", styleId: worst.s.id, ratio: worst.r.ratio } : null;
}
